import * as R from 'remeda';
import {
  Divider,
  Paper,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import {
  ChartsTooltipContainer,
  ChartsTooltipProps,
  useAxesTooltip,
} from '@rossum/ui/x-charts-pro';

type AxesTooltipData = NonNullable<ReturnType<typeof useAxesTooltip>>;
type SeriesItem = AxesTooltipData[number]['seriesItems'][number];

const hasValue = (item: SeriesItem) =>
  R.isNonNullish(item.formattedValue) && item.formattedValue !== '';

const getVisibleSeriesItems = (
  seriesItems: SeriesItem[],
  seriesGroups: (string | number)[][]
) =>
  seriesItems.filter(item => {
    // find the relevant group
    const group = seriesGroups.find(ids => ids.includes(item.seriesId));
    if (!group) return true;

    // get item correspondents of the group
    const groupItems = seriesItems.filter(({ seriesId }) =>
      group.includes(seriesId)
    );
    // if any of the series has value always render that one, otherwise render the the first one in group as an empty placeholder
    return groupItems.some(hasValue)
      ? hasValue(item)
      : item.seriesId === group[0];
  });

type CustomTooltipProps = ChartsTooltipProps & {
  getSeriesStyles?: (seriesId: string | number) => SxProps<Theme>;
  /**
   * Series in the same group are one value drawn in different styles (eg. estimated vs
   * rough precision). Only the one holding a value for the hovered point is rendered;
   * when neither holds one, a single placeholder row is left. The placeholder row carries **the label of the first item** in the group
   *
   * e.g. in the following group if neither series have any value the placeholder will read "Precision -":
   * ```
   * [[ "precision-series-id", // carrying the label "Precision"
   *    "rough-precision-series-id"
   * ]]
   * ```
   */
  seriesGroups?: (string | number)[][];
};

export const CustomTooltip = ({
  getSeriesStyles,
  seriesGroups = [],
}: CustomTooltipProps) => {
  const axesData = useAxesTooltip();

  if (!axesData) return null;

  return (
    <ChartsTooltipContainer>
      <Paper elevation={1} sx={{ borderRadius: borderRadius('m'), p: 0 }}>
        {axesData.map(({ axisId, axisFormattedValue, seriesItems }) => (
          <Stack key={axisId}>
            <Stack sx={{ px: 1.5, py: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {axisFormattedValue}
              </Typography>
            </Stack>
            <Divider />
            <Stack spacing={1} useFlexGap sx={{ p: 1 }}>
              {getVisibleSeriesItems(seriesItems, seriesGroups).map(item => {
                const styles = getSeriesStyles?.(item.seriesId);
                return (
                  <Stack
                    key={item.seriesId}
                    direction="row"
                    spacing={2}
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                    useFlexGap
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center' }}
                    >
                      <Stack
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: borderRadius('xl'),
                          backgroundColor: item.color,
                          flexShrink: 0,
                          ...styles,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {item.formattedLabel}
                      </Typography>
                    </Stack>
                    <Typography variant="subtitle2">
                      {item.formattedValue ?? '-'}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        ))}
      </Paper>
    </ChartsTooltipContainer>
  );
};
